import ArrowRightIcon from "@redotech/redo-icons/arrow-right.svg";
import CheckIcon from "@redotech/redo-icons/check.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import type { ComponentType, ReactNode } from "react";
import { Fragment, memo } from "react";
import * as styles from "./managed-returns-trial-modal.module.css";

export const StepHeading = memo(function StepHeading({
  eyebrow,
  title,
  intro,
}: {
  eyebrow?: string;
  title: ReactNode;
  intro?: ReactNode;
}) {
  return (
    <Flex dir="column" gap="sm">
      {eyebrow && (
        <Text fontSize="xs" fontWeight="semibold" textColor="tertiary">
          {eyebrow}
        </Text>
      )}
      <Text
        as="h1"
        fontFamily="serif"
        fontSize="4xl"
        fontWeight="regular"
        textColor="primary"
      >
        {title}
      </Text>
      {intro && (
        <Text as="div" fontSize="md" textColor="tertiary">
          {intro}
        </Text>
      )}
    </Flex>
  );
});

export const SubHeading = memo(function SubHeading({
  children,
}: {
  children: ReactNode;
}) {
  return <h2 className={styles.subHeading}>{children}</h2>;
});

export const SelectableCard = memo(function SelectableCard({
  title,
  description,
  badge,
  selected,
  onSelect,
}: {
  title: string;
  description: string;
  badge?: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <Flex
      align="center"
      aria-pressed={selected}
      as="button"
      className={
        selected
          ? `${styles.selectableCard} ${styles.selectableCardSelected}`
          : styles.selectableCard
      }
      gap="xl"
      justify="space-between"
      onClick={onSelect}
      p="xl"
      type="button"
      w="full"
    >
      <Flex align="flex-start" dir="column" gap="xs">
        {badge && (
          <Flex alignSelf="flex-start">
            <RedoBadge color="blue" size="sm" text={badge} />
          </Flex>
        )}
        <Flex align="center" gap="sm" wrap="wrap">
          <Text
            as="span"
            fontSize="sm"
            fontWeight="semibold"
            textColor="primary"
          >
            {title}
          </Text>
        </Flex>
        <Text as="span" fontSize="xs" textColor="tertiary">
          {description}
        </Text>
      </Flex>
      <span
        className={
          selected
            ? `${styles.radioDot} ${styles.radioDotSelected}`
            : styles.radioDot
        }
      >
        {selected && <CheckIcon className={styles.radioDotSvg} />}
      </span>
    </Flex>
  );
});

export const FieldCard = memo(function FieldCard({
  title,
  titleAccessory,
  description,
  children,
}: {
  title: string;
  titleAccessory?: ReactNode;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.fieldCard}>
      <div className={styles.fieldHeader}>
        <div className={styles.inlineTitle}>
          <h3 className={styles.fieldTitle}>{title}</h3>
          {titleAccessory}
        </div>
        {description && (
          <p className={styles.fieldDescription}>{description}</p>
        )}
      </div>
      {children}
    </div>
  );
});

export const CheckList = memo(function CheckList({
  items,
}: {
  items: ReactNode[];
}) {
  return (
    <ul className={styles.checkList}>
      {items.map((item, index) => (
        <li className={styles.listItem} key={index}>
          <span className={styles.checkIcon}>
            <CheckIcon className={styles.checkSvg} />
          </span>
          <span className={styles.checkText}>{item}</span>
        </li>
      ))}
    </ul>
  );
});

export const Bullets = memo(function Bullets({ items }: { items: string[] }) {
  return (
    <ul className={styles.bullets}>
      {items.map((item) => (
        <li className={styles.listItem} key={item}>
          <span className={styles.bullet} />
          <span className={styles.bulletText}>{item}</span>
        </li>
      ))}
    </ul>
  );
});

export const Callout = memo(function Callout({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.callout}>
      <span className={styles.calloutLabel}>{label}</span>
      <div className={styles.calloutBody}>{children}</div>
    </div>
  );
});

export const NoteCard = memo(function NoteCard({
  icon,
  title,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  children: ReactNode;
}) {
  const NoteIcon = icon;
  return (
    <div className={styles.noteCard}>
      <div className={styles.inlineTitle}>
        <NoteIcon className={styles.noteIcon} />
        <h4 className={styles.noteTitle}>{title}</h4>
      </div>
      <div className={styles.noteBody}>{children}</div>
    </div>
  );
});

export interface FlowStep {
  icon: ComponentType<{ className?: string }>;
  label: string;
  sub?: string;
}

export const ProcessFlow = memo(function ProcessFlow({
  steps,
}: {
  steps: FlowStep[];
}) {
  return (
    <Flex
      align="stretch"
      className={styles.flow}
      dir="row"
      gap="lg"
      overflowX="auto"
    >
      {steps.map((flowStep, index) => {
        const StepIcon = flowStep.icon;
        return (
          <Fragment key={flowStep.label}>
            <Flex
              align="center"
              className={styles.flowStep}
              dir="column"
              gap="sm"
              p="lg"
            >
              <RedoFeaturedIcon
                color="black"
                Icon={StepIcon}
                shape="circle"
                size="sm"
                type="high-contrast"
              />
              <Flex align="center" dir="column" gap="xxs">
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  textAlign="center"
                  textColor="primary"
                >
                  {flowStep.label}
                </Text>
                {flowStep.sub && (
                  <Text fontSize="xs" textAlign="center" textColor="secondary">
                    {flowStep.sub}
                  </Text>
                )}
              </Flex>
            </Flex>
            {index < steps.length - 1 && (
              <Flex
                align="center"
                alignSelf="center"
                className={styles.flowArrow}
                shrink={0}
              >
                <ArrowRightIcon />
              </Flex>
            )}
          </Fragment>
        );
      })}
    </Flex>
  );
});
